import React from 'react';
import { getFileExtension, getFileExtensionLabel, getFileIconKind } from '../../utils/fileUtils';

const SIZE_MAP = {
  sm: { box: 'w-8 h-8', text: 'text-[8px]', icon: 'w-3.5 h-3.5' },
  md: { box: 'w-10 h-10', text: 'text-[9px]', icon: 'w-4 h-4' },
  lg: { box: 'w-12 h-12', text: 'text-[10px]', icon: 'w-5 h-5' },
};

/** Brand-ish colors for known office / media types. */
export const FILE_TYPE_VISUAL = {
  pdf: { box: 'bg-red-500 text-white shadow-sm shadow-red-500/30' },
  doc: { box: 'bg-blue-600 text-white shadow-sm shadow-blue-600/30' },
  sheet: { box: 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/30' },
  slides: { box: 'bg-orange-500 text-white shadow-sm shadow-orange-500/30' },
  text: { box: 'bg-slate-500 text-white shadow-sm shadow-slate-500/30' },
  archive: { box: 'bg-amber-600 text-white shadow-sm shadow-amber-600/30' },
  audio: { box: 'bg-purple-600 text-white shadow-sm shadow-purple-600/30' },
  video: { box: 'bg-rose-600 text-white shadow-sm shadow-rose-600/30' },
  code: { box: 'bg-indigo-600 text-white shadow-sm shadow-indigo-600/30' },
  image: { box: 'bg-sky-500 text-white shadow-sm shadow-sky-500/30' },
  unknown: {
    box: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-300/80 dark:border-gray-600',
  },
};

const LABEL_BY_EXT = {
  pdf: 'PDF',
  doc: 'DOC',
  docx: 'DOCX',
  rtf: 'RTF',
  odt: 'ODT',
  xls: 'XLS',
  xlsx: 'XLSX',
  csv: 'CSV',
  ods: 'ODS',
  ppt: 'PPT',
  pptx: 'PPTX',
  odp: 'ODP',
  txt: 'TXT',
  md: 'MD',
  zip: 'ZIP',
  rar: 'RAR',
  '7z': '7Z',
  tar: 'TAR',
  gz: 'GZ',
  mp3: 'MP3',
  wav: 'WAV',
  m4a: 'M4A',
  aac: 'AAC',
  ogg: 'OGG',
  flac: 'FLAC',
  mp4: 'MP4',
  mov: 'MOV',
  avi: 'AVI',
  webm: 'WEBM',
  mkv: 'MKV',
  json: 'JSON',
  xml: 'XML',
  yaml: 'YML',
  yml: 'YML',
  js: 'JS',
  jsx: 'JSX',
  ts: 'TS',
  tsx: 'TSX',
  py: 'PY',
  java: 'JAVA',
  go: 'GO',
  html: 'HTML',
  css: 'CSS',
  png: 'PNG',
  jpg: 'JPG',
  jpeg: 'JPEG',
  gif: 'GIF',
  webp: 'WEBP',
};

const FALLBACK_LABEL = {
  pdf: 'PDF',
  doc: 'DOC',
  sheet: 'XLS',
  slides: 'PPT',
  text: 'TXT',
  archive: 'ZIP',
  audio: 'AUD',
  video: 'VID',
  code: 'CODE',
  image: 'IMG',
};

export const resolveFileTypeKind = ({ kind, extension, fileName, fileType, attachment } = {}) => {
  if (kind && kind !== 'file') return kind;
  const type = String(fileType || attachment?.fileType || '').toUpperCase();
  if (type === 'IMAGE') return 'image';
  if (type === 'VIDEO') return 'video';
  if (type === 'AUDIO') return 'audio';
  const ext = (
    extension
    || getFileExtension(fileName || attachment?.fileName || '')
    || ''
  ).toLowerCase().replace(/^\./, '');
  const fromExt = getFileIconKind(ext);
  return fromExt === 'file' ? 'unknown' : fromExt;
};

/**
 * Colorful file-type badge.
 * Known types (pdf / docx / pptx / …) get brand colors.
 * Unknown types show only the extension on a grey background.
 */
const FileTypeIcon = ({
  kind,
  extension,
  fileName,
  fileType,
  attachment,
  size = 'md',
  className = '',
}) => {
  const resolvedKind = resolveFileTypeKind({ kind, extension, fileName, fileType, attachment });
  const visual = FILE_TYPE_VISUAL[resolvedKind] || FILE_TYPE_VISUAL.unknown;
  const sizeCls = SIZE_MAP[size] || SIZE_MAP.md;
  const ext = (
    extension
    || getFileExtensionLabel(fileName || attachment?.fileName || '')
    || getFileExtension(fileName || attachment?.fileName || '')
    || ''
  ).toLowerCase().replace(/^\./, '');

  const label = resolvedKind === 'unknown'
    ? (ext ? ext.slice(0, 5).toUpperCase() : '?')
    : (LABEL_BY_EXT[ext] || FALLBACK_LABEL[resolvedKind] || resolvedKind.toUpperCase());

  return (
    <div
      className={`${sizeCls.box} rounded-lg flex items-center justify-center font-bold uppercase tracking-wide ${visual.box} ${sizeCls.text} ${className}`}
      title={label}
      aria-hidden="true"
    >
      <span className="leading-none px-0.5 truncate max-w-full">{label}</span>
    </div>
  );
};

export default FileTypeIcon;
